import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Typography, Form, DatePicker, Select, Button, Space, Table, Tag, Segmented, Drawer, Card, Tabs, Checkbox, message,
} from 'antd';
import { TeamOutlined, DownloadOutlined, TagOutlined, FileTextOutlined, UnorderedListOutlined, AppstoreOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import api from '../api';
import ResizableTitle from '../components/ResizableTitle';
import { SPACER_COL, STICKY_OFFSET } from '../utils/tableLayout';

import { selectFilter } from '../utils/selectFilter';
const { RangePicker } = DatePicker;

// Coaching session status, matching the error detail page so the same link
// reads the same in both places.
const SESSION_STATUS_COLOR = { draft: 'default', active: 'blue', completed: 'green', canceled: 'red' };
const SESSION_STATUS_LABEL = { draft: 'Draft', active: 'Active', completed: 'Completed', canceled: 'Canceled' };

// The tabs whose data is grouped by the By Campaign / By Error Form / By Agent
// toggle. The rest read question-stats, which has no such grouping.
const VIEW_MODE_TABS = new Set(['agents', 'month']);

const STATUS_COLOR = {
  assigned:          'blue',
  in_progress:       'orange',
  completed:         'green',
  rescore_requested: 'purple',
  canceled:          'default',
};

const STATUS_LABEL = {
  assigned:          'Draft',
  in_progress:       'In Progress',
  completed:         'Completed',
  rescore_requested: 'Disputed',
  canceled:          'Canceled',
};

const METRIC_COLS = [
  {
    title: 'Qty Pending',
    dataIndex: 'PendingCount',
    key: 'PendingCount',
    width: 120,
    align: 'center',
    sorter: (a, b) => a.PendingCount - b.PendingCount,
    render: (v) => v > 0 ? <Tag color="blue">{v}</Tag> : <span style={{ color: '#bfbfbf' }}>0</span>,
  },
  {
    title: 'Qty In Dispute',
    dataIndex: 'DisputedCount',
    key: 'DisputedCount',
    width: 120,
    align: 'center',
    sorter: (a, b) => a.DisputedCount - b.DisputedCount,
    render: (v) => v > 0 ? <Tag color="purple">{v}</Tag> : <span style={{ color: '#bfbfbf' }}>0</span>,
  },
  {
    title: 'Qty Completed',
    dataIndex: 'CompletedCount',
    key: 'CompletedCount',
    width: 130,
    align: 'center',
    sorter: (a, b) => a.CompletedCount - b.CompletedCount,
    render: (v) => v > 0 ? <Tag color="green">{v}</Tag> : <span style={{ color: '#bfbfbf' }}>0</span>,
  },
  {
    title: 'Total Errors',
    dataIndex: 'TotalErrors',
    key: 'TotalErrors',
    width: 120,
    align: 'right',
    sorter: (a, b) => a.TotalErrors - b.TotalErrors,
    render: (v) => v > 0 ? <Tag color="red">{v}</Tag> : <span style={{ color: '#bfbfbf' }}>0</span>,
  },
  {
    title: 'Total Feedbacks',
    dataIndex: 'TotalFeedbacks',
    key: 'TotalFeedbacks',
    width: 130,
    align: 'right',
    sorter: (a, b) => a.TotalFeedbacks - b.TotalFeedbacks,
    render: (v) => v > 0 ? <Tag color="orange">{v}</Tag> : <span style={{ color: '#bfbfbf' }}>0</span>,
  },
];

const DRILL_COLS = (navigate) => [
  {
    title: 'Status',
    dataIndex: 'Status',
    key: 'Status',
    width: 120,
    render: (s) => <Tag color={STATUS_COLOR[s] ?? 'default'}>{STATUS_LABEL[s] ?? s}</Tag>,
  },
  { title: 'Date of Error', dataIndex: 'DateOfError', key: 'DateOfError', width: 115 },
  { title: 'Campaign', dataIndex: 'Campaign_Name', key: 'Campaign_Name', width: 160, ellipsis: true },
  { title: 'Form',     dataIndex: 'FormName',      key: 'FormName',      width: 220, ellipsis: true },
  {
    title: 'Errors',
    dataIndex: 'TotalErrors',
    key: 'TotalErrors',
    width: 90,
    align: 'right',
    render: (v) => v > 0 ? <Tag color="red">{v}</Tag> : <span style={{ color: '#bfbfbf' }}>0</span>,
  },
  {
    title: 'Feedbacks',
    dataIndex: 'TotalFeedbacks',
    key: 'TotalFeedbacks',
    width: 100,
    align: 'right',
    render: (v) => v > 0 ? <Tag color="orange">{v}</Tag> : <span style={{ color: '#bfbfbf' }}>0</span>,
  },
  { title: 'Assigned To', dataIndex: 'AssignedToName', key: 'AssignedToName', width: 150 },
  {
    title: '',
    key: 'view',
    width: 60,
    render: (_, r) => (
      <Button size="small" type="link" onClick={(e) => { e.stopPropagation(); navigate(`/errors/${r.ID}`); }}>
        View
      </Button>
    ),
  },
  SPACER_COL,
];

export default function ErrorAgentStatsPage() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [rows, setRows]           = useState([]);
  const [loading, setLoading]     = useState(false);
  const [forms, setForms]         = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [viewMode, setViewMode]   = useState('campaign');
  const [colWidths, setColWidths] = useState({});
  const [drillOpen, setDrillOpen]       = useState(false);
  const [drillTitle, setDrillTitle]     = useState('');
  const [drillRows, setDrillRows]       = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);
  const [pivotRows, setPivotRows]       = useState([]);
  const [pivotLoading, setPivotLoading] = useState(false);
  const [weekRows, setWeekRows]         = useState([]);
  const [weekLoading, setWeekLoading]   = useState(false);
  const [qStatsRows, setQStatsRows]           = useState([]);
  const [qStatsAgentId, setQStatsAgentId]     = useState(null);
  const [qStatsSection, setQStatsSection]     = useState(null);
  const [qStatsQuestion, setQStatsQuestion]   = useState(null);
  const [qStatsLoading, setQStatsLoading]     = useState(false);
  const [qStatsMode, setQStatsMode]           = useState('summary');
  const [sectionDateRows, setSectionDateRows] = useState([]);
  const [sectionDateAgentId, setSectionDateAgentId] = useState(null);
  const [sectionDateSection, setSectionDateSection]  = useState(null);
  const [sectionDateLoading, setSectionDateLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('agents');
  const [onlyRepeatSections, setOnlyRepeatSections] = useState(false);

  const toResizable = (col) => ({
    ...col,
    width: colWidths[col.key] ?? col.width ?? 120,
    onHeaderCell: (column) => ({
      width: column.width,
      onResize: (_, { size }) => setColWidths((p) => ({ ...p, [col.key]: size.width })),
    }),
  });

  useEffect(() => {
    api.get('/api/errors/filter-options')
      .then((r) => {
        setCampaigns(r.data.campaigns.map((c) => ({ label: c.Name, value: c.ID })));
        setForms(r.data.forms.map((f) => ({ label: f.name, value: f.name })));
      })
      .catch(() => {});
  }, []);

  const buildParams = useCallback((extra = {}) => {
    const values = form.getFieldsValue();
    const p = { ...extra };
    if (values.dateRange?.[0])  p.start_date = values.dateRange[0].format('YYYY-MM-DD');
    if (values.dateRange?.[1])  p.end_date   = values.dateRange[1].format('YYYY-MM-DD');
    if (values.campaigns?.length) p.campaigns = values.campaigns.join(',');
    if (values.form_id)           p.form_name = values.form_id;
    return p;
  }, [form]);

  const fetchStats = useCallback(async () => {
    setRows([]);
    setLoading(true);
    try {
      const res = await api.get('/api/errors/agent-stats', { params: buildParams({ group_by: viewMode }) });
      setRows(res.data);
    } catch {
      // stay empty
    } finally {
      setLoading(false);
    }
  }, [buildParams, viewMode]);

  const fetchWeekPivot = useCallback(async () => {
    setWeekRows([]);
    setWeekLoading(true);
    try {
      const res = await api.get('/api/errors/agent-stats', { params: buildParams({ group_by: 'pivot', pivot_by: viewMode, period: 'week' }) });
      setWeekRows(res.data);
    } catch {
      setWeekRows([]);
    } finally {
      setWeekLoading(false);
    }
  }, [buildParams, viewMode]);

  const fetchPivot = useCallback(async () => {
    setPivotRows([]);
    setPivotLoading(true);
    try {
      const res = await api.get('/api/errors/agent-stats', { params: buildParams({ group_by: 'pivot', pivot_by: viewMode }) });
      setPivotRows(res.data);
    } catch {
      setPivotRows([]);
    } finally {
      setPivotLoading(false);
    }
  }, [buildParams, viewMode]);

  useEffect(() => {
    form.setFieldsValue({ dateRange: [dayjs().startOf('month'), dayjs()] });
  }, []); // eslint-disable-line

  const fetchQStats = useCallback(async () => {
    setQStatsRows([]);
    // The filter lists names from the previous result set, so drop it when
    // those rows go away rather than leaving a selection that hides everything.
    setQStatsAgentId(null);
    setQStatsSection(null);
    setQStatsQuestion(null);
    setQStatsLoading(true);
    try {
      const apiMode = qStatsMode === 'month_pivot' ? 'month' : 'summary';
      const res = await api.get('/api/errors/question-stats', { params: buildParams({ group_by: apiMode }) });
      setQStatsRows(res.data);
    } catch {
      setQStatsRows([]);
    } finally {
      setQStatsLoading(false);
    }
  }, [buildParams, qStatsMode]);

  const fetchSectionDate = useCallback(async () => {
    setSectionDateRows([]);
    // The filter lists names from the previous result set, so drop it when
    // those rows go away rather than leaving a selection that hides everything.
    setSectionDateAgentId(null);
    setSectionDateSection(null);
    setSectionDateLoading(true);
    try {
      const res = await api.get('/api/errors/question-stats', { params: buildParams({ group_by: 'section_date' }) });
      setSectionDateRows(res.data);
    } catch {
      setSectionDateRows([]);
    } finally {
      setSectionDateLoading(false);
    }
  }, [buildParams]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchPivot(); }, [fetchPivot]);
  useEffect(() => { fetchWeekPivot(); }, [fetchWeekPivot]);
  useEffect(() => { fetchQStats(); }, [fetchQStats]);
  useEffect(() => { fetchSectionDate(); }, [fetchSectionDate]);

  const openDrill = useCallback(async (record) => {
    const label =
      record.Campaign ? `${record.AgentName} — ${record.Campaign}` :
      record.FormName ? `${record.AgentName} — ${record.FormName}` :
      record.AgentName;
    setDrillTitle(label);
    setDrillOpen(true);
    setDrillLoading(true);
    setDrillRows([]);
    try {
      const values = form.getFieldsValue();
      // Rows are grouped by the linked QAUsers record, so drill by ID when there
      // is one; AgentName is that user's current display name and would not
      // match the name snapshotted on the submission.
      const p = record.AgentUserID
        ? { agent_user_id: record.AgentUserID }
        : { agent_name: record.AgentName };
      if (values.dateRange?.[0]) p.start_date = values.dateRange[0].format('YYYY-MM-DD');
      if (values.dateRange?.[1]) p.end_date   = values.dateRange[1].format('YYYY-MM-DD');
      if (record.CampaignID) p.campaigns = record.CampaignID;
      else if (values.campaigns?.length) p.campaigns = values.campaigns.join(',');
      if (record.FormID) p.form_id = record.FormID;
      else if (values.form_id) p.form_name = values.form_id;
      const res = await api.get('/api/errors/agent-stats/drill', { params: p });
      setDrillRows(res.data);
    } catch {
      setDrillRows([]);
    } finally {
      setDrillLoading(false);
    }
  }, [form]);

  const agentCol = {
    title: 'Agent Name',
    dataIndex: 'AgentName',
    key: 'AgentName',
    sorter: (a, b) => (a.AgentName ?? '').localeCompare(b.AgentName ?? ''),
  };

  const campaignCol = {
    title: 'Campaign',
    dataIndex: 'Campaign',
    key: 'Campaign',
    width: 180,
    sorter: (a, b) => (a.Campaign ?? '').localeCompare(b.Campaign ?? ''),
  };

  const formCol = {
    title: 'Error Form',
    key: 'Form',
    width: 220,
    sorter: (a, b) => (a.FormName ?? '').localeCompare(b.FormName ?? ''),
    render: (_, r) => r.FormName ?? '—',
  };

  const secondaryCol =
    viewMode === 'campaign' ? campaignCol :
    viewMode === 'form'     ? formCol : null;

  const columns = (secondaryCol
    ? [agentCol, secondaryCol, ...METRIC_COLS]
    : [agentCol, ...METRIC_COLS]
  ).map(toResizable);

  const tableRowKey =
    viewMode === 'campaign' ? (r) => `${r.AgentName}|${r.Campaign}` :
    viewMode === 'form'     ? (r) => `${r.AgentName}|${r.FormID}`   :
    'AgentName';

  const tableSummary = () => {
    const totPending   = rows.reduce((s, r) => s + (r.PendingCount   ?? 0), 0);
    const totDisputed  = rows.reduce((s, r) => s + (r.DisputedCount  ?? 0), 0);
    const totDone      = rows.reduce((s, r) => s + (r.CompletedCount ?? 0), 0);
    const totErrors    = rows.reduce((s, r) => s + (r.TotalErrors    ?? 0), 0);
    const totFeedbacks = rows.reduce((s, r) => s + (r.TotalFeedbacks ?? 0), 0);
    const bold         = { fontWeight: 600 };
    const off          = secondaryCol ? 1 : 0;
    return (
      <Table.Summary fixed>
        <Table.Summary.Row style={{ background: '#fafafa' }}>
          <Table.Summary.Cell index={0} style={bold}>Total</Table.Summary.Cell>
          {secondaryCol && <Table.Summary.Cell index={1} />}
          <Table.Summary.Cell index={1 + off} align="center" style={bold}>
            {totPending  > 0 ? totPending  : <span style={{ color: '#bfbfbf' }}>0</span>}
          </Table.Summary.Cell>
          <Table.Summary.Cell index={2 + off} align="center" style={bold}>
            {totDisputed > 0 ? totDisputed : <span style={{ color: '#bfbfbf' }}>0</span>}
          </Table.Summary.Cell>
          <Table.Summary.Cell index={3 + off} align="center" style={bold}>
            {totDone     > 0 ? totDone     : <span style={{ color: '#bfbfbf' }}>0</span>}
          </Table.Summary.Cell>
          <Table.Summary.Cell index={4 + off} align="right" style={bold}>
            {totErrors > 0 ? <Tag color="red">{totErrors}</Tag> : <span style={{ color: '#bfbfbf' }}>0</span>}
          </Table.Summary.Cell>
          <Table.Summary.Cell index={5 + off} align="right" style={bold}>
            {totFeedbacks > 0 ? <Tag color="orange">{totFeedbacks}</Tag> : <span style={{ color: '#bfbfbf' }}>0</span>}
          </Table.Summary.Cell>
        </Table.Summary.Row>
      </Table.Summary>
    );
  };

  const exportToExcel = () => {
    const exportRows = rows.map((r) => {
      const base = { 'Agent Name': r.AgentName };
      if (viewMode === 'campaign') base['Campaign']   = r.Campaign;
      if (viewMode === 'form')     base['Error Form'] = r.FormName ?? '—';
      base['Qty Pending']           = r.PendingCount;
      base['Qty In Dispute']         = r.DisputedCount;
      base['Qty Completed']         = r.CompletedCount;
      base['Total Errors']    = r.TotalErrors;
      base['Total Feedbacks'] = r.TotalFeedbacks;
      return base;
    });
    const ws = XLSX.utils.json_to_sheet(exportRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Error Agent Stats');
    XLSX.writeFile(wb, 'error-agent-stats.xlsx');
  };

  // Section history is the longest table on the page, so it gets its own agent
  // filter. The options come from the rows already loaded rather than from the
  // filter-options endpoint, so the list only ever offers names that are
  // actually in the table -- picking one can never empty it.
  // Keyed on AgentUserID. AgentName is the QAUsers display name the API
  // resolved for that id and is a label only -- it is never a key here.
  const sectionDateAgents = useMemo(() => {
    const seen = new Map();
    sectionDateRows.forEach((r) => {
      if (r.AgentUserID != null && !seen.has(r.AgentUserID)) {
        seen.set(r.AgentUserID, r.AgentName);
      }
    });
    return [...seen.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
  }, [sectionDateRows]);

  // Sections present in the current rows. Keyed on the name, which is what the
  // rows carry -- there is no section id in this feed.
  const sectionDateSections = useMemo(() => (
    [...new Set(sectionDateRows.map((r) => r.SectionName).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b))
      .map((name) => ({ value: name, label: name }))
  ), [sectionDateRows]);

  // Coaching is raised once an agent reaches this many errors on one section
  // within the period being looked at, so the filter counts errors rather than
  // rows -- a submission can carry more than one error on a section where the
  // form allows it.
  const COACHING_THRESHOLD = 3;

  // Narrowing to one section cannot change any agent+section total, since each
  // total is already per section -- so the 3+ filter and its count stay honest
  // whether or not a section is picked.
  const sectionDateFiltered = useMemo(() => sectionDateRows.filter((r) => (
    (sectionDateAgentId == null || r.AgentUserID === sectionDateAgentId) &&
    (sectionDateSection == null || r.SectionName === sectionDateSection)
  )), [sectionDateRows, sectionDateAgentId, sectionDateSection]);

  const sectionDateVisible = useMemo(() => {
    const byAgent = sectionDateFiltered;
    if (!onlyRepeatSections) return byAgent;

    // Keyed on AgentUserID, not the name, so two people sharing a display name
    // are never merged into one running total.
    const totals = {};
    byAgent.forEach((r) => {
      const k = `${r.AgentUserID ?? `n:${r.AgentName}`}||${r.SectionName}`;
      totals[k] = (totals[k] || 0) + (r.TotalErrors || 0);
    });
    // Every row of a qualifying agent+section is kept, not just the ones
    // carrying errors: the point is to see the submissions behind the count,
    // and a feedback-only row is part of that section's history.
    return byAgent.filter((r) => {
      const k = `${r.AgentUserID ?? `n:${r.AgentName}`}||${r.SectionName}`;
      return (totals[k] || 0) >= COACHING_THRESHOLD;
    });
  }, [sectionDateFiltered, onlyRepeatSections]);

  // How many agent+section pairs cleared the threshold -- the number of
  // coaching conversations the period implies.
  const repeatSectionCount = useMemo(() => {
    const byAgent = sectionDateFiltered;
    const totals = {};
    byAgent.forEach((r) => {
      const k = `${r.AgentUserID ?? `n:${r.AgentName}`}||${r.SectionName}`;
      totals[k] = (totals[k] || 0) + (r.TotalErrors || 0);
    });
    return Object.values(totals).filter((n) => n >= COACHING_THRESHOLD).length;
  }, [sectionDateFiltered]);

  // Section Stats and Question Stats read the same feed and already share a
  // mode, so they share one agent filter too: pick a name on either tab and
  // the other stays on that agent. Options come from the rows already loaded,
  // so picking one can never empty the table. Keyed on AgentUserID; AgentName
  // is the QAUsers display name the API resolved and is a label only.
  const qStatsAgents = useMemo(() => {
    const seen = new Map();
    qStatsRows.forEach((r) => {
      if (r.AgentUserID != null && !seen.has(r.AgentUserID)) seen.set(r.AgentUserID, r.AgentName);
    });
    return [...seen.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
  }, [qStatsRows]);

  const qStatsVisible = useMemo(() => (
    qStatsAgentId == null ? qStatsRows : qStatsRows.filter((r) => r.AgentUserID === qStatsAgentId)
  ), [qStatsRows, qStatsAgentId]);

  // Section Stats and Question Stats share the section filter as well as the
  // agent one; the options narrow with the agent already picked.
  const qStatsSections = useMemo(() => (
    [...new Set(qStatsVisible.map((r) => r.SectionName).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b))
      .map((name) => ({ value: name, label: name }))
  ), [qStatsVisible]);

  const qStatsSectionVisible = useMemo(() => (
    qStatsSection == null ? qStatsVisible : qStatsVisible.filter((r) => r.SectionName === qStatsSection)
  ), [qStatsVisible, qStatsSection]);

  // Question Stats adds a third filter on top of agent and section. Section
  // Stats does not see this one: a question narrows to part of a section, so
  // applying it there would report a section total that is not the section's.
  const qStatsQuestions = useMemo(() => (
    [...new Set(qStatsSectionVisible.map((r) => r.QuestionText).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b))
      .map((q) => ({ value: q, label: q }))
  ), [qStatsSectionVisible]);

  const qStatsQuestionVisible = useMemo(() => (
    qStatsQuestion == null ? qStatsSectionVisible : qStatsSectionVisible.filter((r) => r.QuestionText === qStatsQuestion)
  ), [qStatsSectionVisible, qStatsQuestion]);

  // The two Selects both cards put in their header.
  const qStatsAgentFilter = (
    <Select
      allowClear
      showSearch
      placeholder="All agents"
      style={{ width: 220 }}
      value={qStatsAgentId}
      onChange={setQStatsAgentId}
      options={qStatsAgents}
      optionFilterProp="label"
    />
  );

  const qStatsSectionFilter = (
    <Select
      allowClear
      showSearch
      placeholder="All sections"
      style={{ width: 220 }}
      value={qStatsSection}
      onChange={setQStatsSection}
      options={qStatsSections}
      optionFilterProp="label"
    />
  );

  // Section Stats in Summary mode rolls the per-question rows up to
  // agent + section. Shared by the table and its export so the file can never
  // disagree with the screen.
  // Side panel: the submissions behind a Section / Question Stats number, on
  // the page's filters. { title, rows }
  const [statDrill, setStatDrill] = useState(null);
  const openStatDrill = (r, kind, withQuestion) => {
    const title = `${r.AgentName} — ${r.SectionName}${withQuestion ? ` › ${r.QuestionText}` : ''} — ${kind === 'feedbacks' ? 'Feedbacks' : 'Errors'}`;
    setStatDrill({ title, rows: null });
    const p = buildParams({
      group_by: 'drill', drill_kind: kind, drill_section: r.SectionName,
      ...(r.AgentUserID != null ? { drill_agent_user_id: r.AgentUserID } : {}),
      ...(withQuestion ? { drill_question: r.QuestionText } : {}),
    });
    api.get('/api/errors/question-stats', { params: p })
      .then((res) => setStatDrill((d) => (d && d.title === title ? { ...d, rows: res.data || [] } : d)))
      .catch(() => { message.error('Failed to load the submissions.'); setStatDrill(null); });
  };

  const sectionSummaryRows = useMemo(() => {
    const map = {};
    qStatsSectionVisible.forEach((r) => {
      const k = `${r.AgentName}||${r.SectionName}`;
      if (!map[k]) map[k] = { AgentUserID: r.AgentUserID, AgentName: r.AgentName, SectionName: r.SectionName, TotalErrors: 0, TotalFeedbacks: 0 };
      map[k].TotalErrors    += r.TotalErrors;
      map[k].TotalFeedbacks += r.TotalFeedbacks ?? 0;
    });
    return Object.values(map);
  }, [qStatsSectionVisible]);

  // Exports what is on screen, so an agent filter narrows the file too.
  const exportSectionHistory = () => {
    const ws = XLSX.utils.json_to_sheet(sectionDateVisible.map((r) => ({
      'Error Submission ID': r.SubmissionID,
      'Date of Error':       r.DateOfError,
      'Agent Name':          r.AgentName,
      'Section':             r.SectionName,
      'Errors':              r.TotalErrors,
      'Feedbacks':           r.TotalFeedbacks,
      // Ids only: a cell holding several sessions has to stay one value, and
      // the id is what makes a session findable afterwards.
      'Coaching Sessions':   (r.Coachings || []).map((c) => `#${c.ID}`).join(', '),
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Error Section History');
    XLSX.writeFile(wb, 'error-section-history.xlsx');
  };

  // Month Over Month and Week Over Week: the same table, from rows keyed by
  // month (YYYY-MM) or by the Monday of the week (YYYY-MM-DD), in MonthKey.
  const monthTitle = (mk) => {
    try { const [y, m] = mk.split('-'); return new Date(+y, +m - 1, 1).toLocaleString('default', { month: 'long', year: 'numeric' }); }
    catch { return mk; }
  };
  // Monday to Sunday, trimmed to the dates searched: "Oct 1–5", "Sep 29–Oct 5".
  const weekTitle = (wk) => {
    const range = form.getFieldValue('dateRange');
    let a = dayjs(wk); let b = a.add(6, 'day');
    if (range?.[0] && a.isBefore(range[0], 'day')) a = range[0];
    if (range?.[1] && b.isAfter(range[1], 'day')) b = range[1];
    if (a.isSame(b, 'day')) return a.format('MMM D');
    return `${a.format('MMM D')}–${b.format(a.isSame(b, 'month') ? 'D' : 'MMM D')}`;
  };
  const buildPivot = (pivotRows, titleOf) => {
  const pivotMonths = [...new Set(pivotRows.map((r) => r.MonthKey))].sort();
  const pivotMonthLabels = {};
  pivotMonths.forEach((mk) => { if (mk) pivotMonthLabels[mk] = titleOf(mk); });

  const byKey = {};
  pivotRows.forEach((r) => {
    const k =
      viewMode === 'campaign' ? `${r.AgentName}||${r.Campaign}` :
      viewMode === 'form'     ? `${r.AgentName}||${r.FormName}` :
      r.AgentName;
    if (!byKey[k]) byKey[k] = { _key: k, AgentName: r.AgentName, Campaign: r.Campaign, FormName: r.FormName };
    byKey[k][r.MonthKey] = { count: r.SubmissionCount, errors: r.TotalErrors, feedbacks: r.TotalFeedbacks ?? 0 };
  });
  const pivotTableRows = Object.values(byKey).sort((a, b) =>
    (a.AgentName ?? '').localeCompare(b.AgentName ?? '') ||
    ((a.Campaign ?? a.FormName ?? '')).localeCompare(b.Campaign ?? b.FormName ?? '')
  );

  const fixedPivotCols = [
    { title: 'Agent Name', dataIndex: 'AgentName', key: '__agent', fixed: 'left', width: 180, ellipsis: true },
    ...(viewMode === 'campaign' ? [
      { title: 'Campaign', dataIndex: 'Campaign', key: '__campaign', fixed: 'left', width: 160, ellipsis: true },
    ] : viewMode === 'form' ? [
      { title: 'Error Form', key: '__form', fixed: 'left', width: 200, ellipsis: true,
        render: (_, r) => r.FormName ?? '—' },
    ] : []),
  ];

  const pivotCols = [
    ...fixedPivotCols,
    ...pivotMonths.map((mk) => ({
      title: pivotMonthLabels[mk] || mk,
      key: mk,
      width: 160,
      align: 'center',
      render: (_, r) => {
        const cell = r[mk];
        if (!cell || cell.count === 0) return <span style={{ color: '#bfbfbf' }}>—</span>;
        const errColor = cell.errors >= 5 ? '#ff4d4f' : cell.errors >= 2 ? '#faad14' : '#52c41a';
        return (
          <div>
            <div style={{ color: errColor, fontWeight: 600 }}>
              {cell.errors} {cell.errors === 1 ? 'error' : 'errors'}
            </div>
            {cell.feedbacks > 0 && (
              <div style={{ color: '#d46b08', fontWeight: 600 }}>
                {cell.feedbacks} {cell.feedbacks === 1 ? 'feedback' : 'feedbacks'}
              </div>
            )}
            <div style={{ fontSize: 11, color: '#8c8c8c' }}>{cell.count} submission{cell.count !== 1 ? 's' : ''}</div>
          </div>
        );
      },
    })),
  ];
  return { pivotMonths, pivotMonthLabels, pivotTableRows, pivotCols };
  };
  const { pivotMonths, pivotMonthLabels, pivotTableRows, pivotCols } = buildPivot(pivotRows, monthTitle);
  const weekPivot = buildPivot(weekRows, weekTitle);

  // The page-level Export follows the tab on screen. One button that always
  // wrote the agent table was a trap: from Section History it silently handed
  // back a different table's file.
  const saveSheet = (data, sheetName, fileName) => {
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data), sheetName);
    XLSX.writeFile(wb, fileName);
  };

  const exportOverTime = ({ pivotTableRows, pivotMonths, pivotMonthLabels }, sheet, file) => {
    saveSheet(pivotTableRows.map((r) => {
      const base = { 'Agent Name': r.AgentName };
      if (viewMode === 'campaign') base['Campaign']   = r.Campaign;
      if (viewMode === 'form')     base['Error Form'] = r.FormName ?? '—';
      // A month is three numbers on screen; a spreadsheet can only sort or sum
      // them if each gets its own column.
      pivotMonths.forEach((mk) => {
        const cell = r[mk] || {};
        const label = pivotMonthLabels[mk] || mk;
        base[`${label} Errors`]      = cell.errors    ?? 0;
        base[`${label} Feedbacks`]   = cell.feedbacks ?? 0;
        base[`${label} Submissions`] = cell.count     ?? 0;
      });
      return base;
    }), sheet, file);
  };
  const exportMonthOverMonth = () => exportOverTime({ pivotTableRows, pivotMonths, pivotMonthLabels },
    'Month Over Month', 'error-month-over-month.xlsx');
  const exportWeekOverWeek = () => exportOverTime(weekPivot, 'Week Over Week', 'error-week-over-week.xlsx');

  // Section and Question stats share qStatsMode, so both exports follow it.
  const qStatsMonths = [...new Set(qStatsSectionVisible.map((r) => r.MonthKey))].sort();
  const qStatsMonthLabel = (mk) => { try { return dayjs(mk + '-01').format('MMM YYYY'); } catch { return mk; } };

  const exportSectionStats = () => {
    if (qStatsMode === 'month_pivot') {
      const map = {}; const order = [];
      qStatsSectionVisible.forEach((r) => {
        const k = `${r.AgentName}||${r.SectionName}`;
        if (!map[k]) { map[k] = { 'Agent Name': r.AgentName, 'Section': r.SectionName }; order.push(k); }
        const label = qStatsMonthLabel(r.MonthKey);
        map[k][`${label} Errors`]    = (map[k][`${label} Errors`]    || 0) + (r.TotalErrors    || 0);
        map[k][`${label} Feedbacks`] = (map[k][`${label} Feedbacks`] || 0) + (r.TotalFeedbacks || 0);
      });
      // Fill the gaps so every row carries every month column.
      const rowsOut = order.map((k) => {
        const row = map[k];
        qStatsMonths.forEach((mk) => {
          const label = qStatsMonthLabel(mk);
          if (row[`${label} Errors`]    == null) row[`${label} Errors`]    = 0;
          if (row[`${label} Feedbacks`] == null) row[`${label} Feedbacks`] = 0;
        });
        return row;
      });
      saveSheet(rowsOut, 'Section Stats', 'error-section-stats.xlsx');
      return;
    }
    saveSheet(sectionSummaryRows.map((r) => ({
      'Agent Name':      r.AgentName,
      'Section':         r.SectionName,
      'Total Errors':    r.TotalErrors,
      'Total Feedbacks': r.TotalFeedbacks,
    })), 'Section Stats', 'error-section-stats.xlsx');
  };

  const exportQuestionStats = () => {
    if (qStatsMode === 'month_pivot') {
      const map = {}; const order = [];
      qStatsQuestionVisible.forEach((r) => {
        const k = `${r.AgentName}||${r.SectionName}||${r.QuestionText}`;
        if (!map[k]) { map[k] = { 'Agent Name': r.AgentName, 'Section': r.SectionName, 'Question': r.QuestionText }; order.push(k); }
        map[k][qStatsMonthLabel(r.MonthKey)] = r.TotalErrors;
      });
      // Months from the question-filtered rows, so a filtered file does not
      // carry all-zero columns for months that question never appeared in.
      const qMonths = [...new Set(qStatsQuestionVisible.map((r) => r.MonthKey))].sort();
      const rowsOut = order.map((k) => {
        const row = map[k];
        qMonths.forEach((mk) => { const label = qStatsMonthLabel(mk); if (row[label] == null) row[label] = 0; });
        return row;
      });
      saveSheet(rowsOut, 'Question Stats', 'error-question-stats.xlsx');
      return;
    }
    saveSheet(qStatsQuestionVisible.map((r) => ({
      'Agent Name':      r.AgentName,
      'Section':         r.SectionName,
      'Question':        r.QuestionText,
      '# Submissions':   r.SubmissionCount,
      'Total Errors':    r.TotalErrors,
      'Total Feedbacks': r.TotalFeedbacks,
    })), 'Question Stats', 'error-question-stats.xlsx');
  };

  const TAB_EXPORTS = {
    agents:    { run: exportToExcel,         count: rows.length },
    month:     { run: exportMonthOverMonth,  count: pivotTableRows.length },
    week:      { run: exportWeekOverWeek,    count: weekPivot.pivotTableRows.length },
    sections:  { run: exportSectionStats,    count: qStatsSectionVisible.length },
    questions: { run: exportQuestionStats,   count: qStatsQuestionVisible.length },
    history:   { run: exportSectionHistory,  count: sectionDateVisible.length },
  };
  const activeExport = TAB_EXPORTS[activeTab] || TAB_EXPORTS.agents;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Error Agent Stats
        </Typography.Title>
        {/* Only on the tabs it changes. It drives group_by / pivot_by on the
            agent-stats endpoint, which the section, question and history tabs
            do not use -- left visible there it would look broken, since
            clicking it would alter nothing on screen. */}
        {VIEW_MODE_TABS.has(activeTab) && (
          <Segmented
            value={viewMode}
            onChange={setViewMode}
            options={[
              { label: 'By Campaign',   value: 'campaign', icon: <TagOutlined /> },
              { label: 'By Error Form', value: 'form',     icon: <FileTextOutlined /> },
              { label: 'By Agent',      value: 'agent',    icon: <TeamOutlined /> },
            ]}
          />
        )}
      </div>

      <Form
        form={form}
        layout="inline"
        onFinish={() => { fetchStats(); fetchPivot(); fetchWeekPivot(); fetchQStats(); fetchSectionDate(); }}
        style={{ marginBottom: 16, flexWrap: 'wrap', gap: 8 }}
      >
        <Form.Item name="dateRange" label="Date of Error">
          <RangePicker style={{ width: 280 }} />
        </Form.Item>

        <Form.Item name="form_id" label="Error Form">
          <Select
            allowClear
            placeholder="All forms"
            options={forms}
            style={{ width: 280 }}
          />
        </Form.Item>

        <Form.Item name="campaigns" label="Campaign">
          <Select
            mode="multiple"
            allowClear
            showSearch filterOption={selectFilter}
            placeholder="All campaigns"
            options={campaigns}
            style={{ width: 260 }}
            maxTagCount="responsive"
          />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={loading || pivotLoading}>
              Filter
            </Button>
            <Button onClick={() => {
              form.resetFields();
              form.setFieldsValue({ dateRange: [dayjs().startOf('month'), dayjs()] });
              fetchStats();
              fetchPivot();
              fetchQStats();
              fetchSectionDate();
            }}>
              Reset
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={activeExport.run}
              disabled={activeExport.count === 0}
            >
              Export
            </Button>
          </Space>
        </Form.Item>
      </Form>

      {/* Five stacked sections made the page long enough that the lower ones
          were rarely reached. Tabs rather than removing anything: the same
          content, one at a time, following campaign-stats-today. The filter
          form stays above them, since it applies to all five. */}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'agents',
            label: 'Agent Stats',
            children: (
              <>
                      <Table
                        rowKey={tableRowKey}
                        dataSource={rows}
                        columns={columns}
                        components={{ header: { cell: ResizableTitle } }}
                        loading={loading}
                        size="small"
                        pagination={{ defaultPageSize: 50, showSizeChanger: true, showTotal: (t) => `${t} rows` }}
                        scroll={{ x: 'max-content' }}
                        summary={rows.length > 0 ? tableSummary : undefined}
                        sticky
                        onRow={(record) => ({ onClick: () => openDrill(record), style: { cursor: 'pointer' } })}
                      />
              </>
            ),
          },
          {
            key: 'month',
            label: 'Month Over Month',
            children: (
              <>
                      <div className="sticky-table-label" style={{ marginTop: 28, marginBottom: 8 }}>
                        <Typography.Title level={5} style={{ margin: 0 }}>
                          Month Over Month (Completed Errors, Date of Error)
                        </Typography.Title>
                      </div>
                      <Table
                        rowKey="_key"
                        dataSource={pivotTableRows}
                        columns={pivotCols}
                        loading={pivotLoading}
                        size="small"
                        pagination={false}
                        sticky={{ offsetHeader: STICKY_OFFSET }}
                        scroll={{ x: 'max-content' }}
                        locale={{ emptyText: 'No data for current filters.' }}
                      />
              </>
            ),
          },
          {
            key: 'week',
            label: 'Week Over Week',
            children: (
              <>
                      <div className="sticky-table-label" style={{ marginTop: 28, marginBottom: 8 }}>
                        <Typography.Title level={5} style={{ margin: 0 }}>
                          Week Over Week (Completed Errors, Date of Error)
                        </Typography.Title>
                      </div>
                      <Table
                        rowKey="_key"
                        dataSource={weekPivot.pivotTableRows}
                        columns={weekPivot.pivotCols}
                        loading={weekLoading}
                        size="small"
                        pagination={false}
                        sticky={{ offsetHeader: STICKY_OFFSET }}
                        scroll={{ x: 'max-content' }}
                        locale={{ emptyText: 'No data for current filters.' }}
                      />
              </>
            ),
          },
          {
            key: 'sections',
            label: 'Section Stats',
            children: (
              <>
                      <Card
                        className="sticky-table-card"
                        title="Error Section Stats (Completed Errors)"
                        size="small"
                        style={{ marginTop: 28 }}
                        extra={
                          <Space>
                            {qStatsAgentFilter}
                            {qStatsSectionFilter}
                            <Segmented
                              size="small"
                              value={qStatsMode}
                              onChange={setQStatsMode}
                              options={[
                                { label: 'Summary',        value: 'summary',     icon: <UnorderedListOutlined /> },
                                { label: 'By Month Pivot', value: 'month_pivot', icon: <AppstoreOutlined /> },
                              ]}
                            />
                          </Space>
                        }
                      >
                        {qStatsMode === 'month_pivot' ? (() => {
                          const months = [...new Set(qStatsSectionVisible.map((r) => r.MonthKey))].sort();
                          const mLabels = {};
                          months.forEach((mk) => { mLabels[mk] = dayjs(mk + '-01').format('MMM YYYY'); });
                          const secMap = {}; const secOrder = []; const agentFirst = {};
                          qStatsSectionVisible.forEach((r) => {
                            const k = `${r.AgentName}||${r.SectionName}`;
                            if (!secMap[k]) {
                              const idx = secOrder.length;
                              secMap[k] = { _key: k, _idx: idx, AgentName: r.AgentName, SectionName: r.SectionName };
                              secOrder.push(k);
                              if (!(r.AgentName in agentFirst)) agentFirst[r.AgentName] = idx;
                            }
                            const prev = secMap[k][r.MonthKey] || { errors: 0, feedbacks: 0 };
                            secMap[k][r.MonthKey] = { errors: prev.errors + (r.TotalErrors || 0), feedbacks: prev.feedbacks + (r.TotalFeedbacks || 0) };
                          });
                          const secPivotData = secOrder
                            .map((k) => secMap[k])
                            .sort((a, b) => {
                              const aA = agentFirst[a.AgentName]; const aB = agentFirst[b.AgentName];
                              if (aA !== aB) return aA - aB;
                              return a._idx - b._idx;
                            });
                          const secCols = [
                            { title: 'Agent Name', dataIndex: 'AgentName', key: '__agent', width: 180, ellipsis: true, fixed: 'left',
                              onCell: (r, i) => {
                                const prev = i > 0 ? secPivotData[i - 1].AgentName : null;
                                if (r.AgentName === prev) return { rowSpan: 0 };
                                let span = 1;
                                while (i + span < secPivotData.length && secPivotData[i + span].AgentName === r.AgentName) span++;
                                return { rowSpan: span };
                              }
                            },
                            { title: 'Section', dataIndex: 'SectionName', key: '__sec', width: 260, ellipsis: true, fixed: 'left' },
                            ...months.map((mk) => ({
                              title: mLabels[mk] || mk, key: mk, width: 120, align: 'center',
                              render: (_, r) => {
                                const cell = r[mk];
                                if (!cell || (cell.errors === 0 && cell.feedbacks === 0)) return <span style={{ color: '#d9d9d9' }}>—</span>;
                                return (
                                  <div>
                                    {cell.errors > 0 && <Tag color="red" style={{ margin: 0 }}>{cell.errors} {cell.errors === 1 ? 'error' : 'errors'}</Tag>}
                                    {cell.feedbacks > 0 && <Tag color="orange" style={{ margin: '2px 0 0' }}>{cell.feedbacks} {cell.feedbacks === 1 ? 'feedback' : 'feedbacks'}</Tag>}
                                  </div>
                                );
                              },
                            })),
                            SPACER_COL,
                          ];
                          return (
                            <Table rowKey="_key" dataSource={secPivotData} columns={secCols} size="small"
                              loading={qStatsLoading} pagination={false} scroll={{ x: 'max-content' }} bordered
                              sticky={{ offsetHeader: STICKY_OFFSET }}
                              locale={{ emptyText: 'No completed error submissions in this date range.' }} />
                          );
                        })() : (
                          <Table
                            rowKey={(r) => `${r.AgentName}|${r.SectionName}`}
                            dataSource={sectionSummaryRows}
                            loading={qStatsLoading}
                            size="small"
                            pagination={{ defaultPageSize: 50, showSizeChanger: true, showTotal: (t) => `${t} rows` }}
                            scroll={{ x: 'max-content' }}
                            sticky={{ offsetHeader: STICKY_OFFSET }}
                            locale={{ emptyText: 'No completed error submissions in this date range.' }}
                            columns={[
                              { title: 'Agent Name', dataIndex: 'AgentName',   key: 'AgentName',   width: 180, ellipsis: true, fixed: 'left',
                                sorter: (a, b) => (a.AgentName ?? '').localeCompare(b.AgentName ?? '') },
                              { title: 'Section',    dataIndex: 'SectionName', key: 'SectionName', width: 260, ellipsis: true,
                                sorter: (a, b) => (a.SectionName ?? '').localeCompare(b.SectionName ?? '') },
                              { title: 'Total Errors', dataIndex: 'TotalErrors', key: 'TotalErrors', width: 110, align: 'right',
                                sorter: (a, b) => (a.TotalErrors ?? 0) - (b.TotalErrors ?? 0),
                                render: (v, r) => v > 0
                                  ? <Tag color="red" style={{ cursor: 'pointer' }} onClick={() => openStatDrill(r, 'errors', false)}>{v}</Tag>
                                  : <span style={{ color: '#bfbfbf' }}>0</span> },
                              { title: 'Total Feedbacks', dataIndex: 'TotalFeedbacks', key: 'TotalFeedbacks', width: 130, align: 'right',
                                sorter: (a, b) => (a.TotalFeedbacks ?? 0) - (b.TotalFeedbacks ?? 0),
                                render: (v, r) => v > 0
                                  ? <Tag color="orange" style={{ cursor: 'pointer' }} onClick={() => openStatDrill(r, 'feedbacks', false)}>{v}</Tag>
                                  : <span style={{ color: '#bfbfbf' }}>0</span> },
                              SPACER_COL,
                            ]}
                          />
                        )}
                      </Card>
              </>
            ),
          },
          {
            key: 'questions',
            label: 'Question Stats',
            children: (
              <>
                      <Card
                        className="sticky-table-card"
                        title="Error Question Stats (Completed Errors)"
                        size="small"
                        style={{ marginTop: 16 }}
                        extra={
                          <Space>
                            {qStatsAgentFilter}
                            {qStatsSectionFilter}
                            <Select
                              allowClear
                              showSearch
                              placeholder="All questions"
                              style={{ width: 260 }}
                              value={qStatsQuestion}
                              onChange={setQStatsQuestion}
                              options={qStatsQuestions}
                              optionFilterProp="label"
                            />
                          </Space>
                        }
                      >
                        {qStatsMode === 'month_pivot' ? (() => {
                          const months = [...new Set(qStatsQuestionVisible.map((r) => r.MonthKey))].sort();
                          const mLabels = {};
                          months.forEach((mk) => { mLabels[mk] = dayjs(mk + '-01').format('MMM YYYY'); });
                          const seen = []; const map = {}; const agentFirst = {}; const secFirst = {};
                          qStatsQuestionVisible.forEach((r) => {
                            const k = `${r.AgentName}||${r.SectionName}||${r.QuestionText}`;
                            if (!map[k]) {
                              const idx = seen.length;
                              map[k] = { _key: k, _idx: idx, AgentName: r.AgentName, SectionName: r.SectionName, QuestionText: r.QuestionText };
                              seen.push(k);
                              if (!(r.AgentName in agentFirst)) agentFirst[r.AgentName] = idx;
                              const sk = `${r.AgentName}||${r.SectionName}`;
                              if (!(sk in secFirst)) secFirst[sk] = idx;
                            }
                            map[k][r.MonthKey] = r.TotalErrors;
                          });
                          const pivotData = seen
                            .map((k) => map[k])
                            .sort((a, b) => {
                              const aA = agentFirst[a.AgentName]; const aB = agentFirst[b.AgentName];
                              if (aA !== aB) return aA - aB;
                              const skA = `${a.AgentName}||${a.SectionName}`; const skB = `${b.AgentName}||${b.SectionName}`;
                              const sA = secFirst[skA]; const sB = secFirst[skB];
                              if (sA !== sB) return sA - sB;
                              return a._idx - b._idx;
                            });
                          const cols = [
                            { title: 'Agent Name', dataIndex: 'AgentName', key: '__agent', width: 180, ellipsis: true, fixed: 'left',
                              onCell: (r, i) => {
                                const prev = i > 0 ? pivotData[i - 1].AgentName : null;
                                if (r.AgentName === prev) return { rowSpan: 0 };
                                let span = 1;
                                while (i + span < pivotData.length && pivotData[i + span].AgentName === r.AgentName) span++;
                                return { rowSpan: span };
                              }
                            },
                            { title: 'Section', dataIndex: 'SectionName', key: '__sec', width: 150, ellipsis: true, fixed: 'left',
                              onCell: (r, i) => {
                                const prevKey = i > 0 ? `${pivotData[i-1].AgentName}|${pivotData[i-1].SectionName}` : null;
                                const currKey = `${r.AgentName}|${r.SectionName}`;
                                if (currKey === prevKey) return { rowSpan: 0 };
                                let span = 1;
                                while (i + span < pivotData.length &&
                                  `${pivotData[i+span].AgentName}|${pivotData[i+span].SectionName}` === currKey) span++;
                                return { rowSpan: span };
                              }
                            },
                            { title: 'Question', dataIndex: 'QuestionText', key: '__q', width: 220, ellipsis: true, fixed: 'left' },
                            ...months.map((mk) => ({
                              title: mLabels[mk] || mk, key: mk, width: 100, align: 'center',
                              render: (_, r) => {
                                const v = r[mk];
                                if (v == null || v === 0) return <span style={{ color: '#d9d9d9' }}>—</span>;
                                return <Tag color="red" style={{ margin: 0 }}>{v}</Tag>;
                              },
                            })),
                            SPACER_COL,
                          ];
                          return (
                            <Table rowKey="_key" dataSource={pivotData} columns={cols} size="small"
                              loading={qStatsLoading} pagination={false} scroll={{ x: 'max-content' }} bordered
                              sticky={{ offsetHeader: STICKY_OFFSET }}
                              locale={{ emptyText: 'No completed error submissions in this date range.' }} />
                          );
                        })() : (
                          <Table
                            rowKey={(r) => `${r.AgentName}|${r.SectionName}|${r.QuestionText}`}
                            dataSource={qStatsQuestionVisible}
                            loading={qStatsLoading}
                            size="small"
                            pagination={{ defaultPageSize: 50, showSizeChanger: true, showTotal: (t) => `${t} rows` }}
                            scroll={{ x: 'max-content' }}
                            sticky={{ offsetHeader: STICKY_OFFSET }}
                            locale={{ emptyText: 'No completed error submissions in this date range.' }}
                            columns={[
                              { title: 'Agent Name', dataIndex: 'AgentName',    key: 'AgentName',    width: 180, ellipsis: true, fixed: 'left',
                                sorter: (a, b) => (a.AgentName ?? '').localeCompare(b.AgentName ?? '') },
                              { title: 'Section',    dataIndex: 'SectionName',  key: 'SectionName',  width: 150, ellipsis: true,
                                sorter: (a, b) => (a.SectionName ?? '').localeCompare(b.SectionName ?? '') },
                              { title: 'Question',   dataIndex: 'QuestionText', key: 'QuestionText', width: 360, ellipsis: true,
                                sorter: (a, b) => (a.QuestionText ?? '').localeCompare(b.QuestionText ?? '') },
                              { title: '# Submissions', dataIndex: 'SubmissionCount', key: 'SubmissionCount', width: 130, align: 'right',
                                sorter: (a, b) => (a.SubmissionCount ?? 0) - (b.SubmissionCount ?? 0) },
                              { title: 'Total Errors', dataIndex: 'TotalErrors', key: 'TotalErrors', width: 110, align: 'right',
                                sorter: (a, b) => (a.TotalErrors ?? 0) - (b.TotalErrors ?? 0),
                                render: (v, r) => v > 0
                                  ? <Tag color="red" style={{ cursor: 'pointer' }} onClick={() => openStatDrill(r, 'errors', true)}>{v}</Tag>
                                  : <span style={{ color: '#bfbfbf' }}>0</span> },
                              { title: 'Total Feedbacks', dataIndex: 'TotalFeedbacks', key: 'TotalFeedbacks', width: 130, align: 'right',
                                sorter: (a, b) => (a.TotalFeedbacks ?? 0) - (b.TotalFeedbacks ?? 0),
                                render: (v, r) => v > 0
                                  ? <Tag color="orange" style={{ cursor: 'pointer' }} onClick={() => openStatDrill(r, 'feedbacks', true)}>{v}</Tag>
                                  : <span style={{ color: '#bfbfbf' }}>0</span> },
                              SPACER_COL,
                            ]}
                          />
                        )}
                      </Card>
              </>
            ),
          },
          {
            key: 'history',
            label: 'Section History',
            children: (
              <>
                      <Card
                        className="sticky-table-card"
                        title="Error Section History (Completed Errors)"
                        size="small"
                        style={{ marginTop: 16 }}
                        extra={
                          <Space>
                            <Select
                              allowClear
                              showSearch
                              placeholder="All agents"
                              style={{ width: 220 }}
                              value={sectionDateAgentId}
                              onChange={setSectionDateAgentId}
                              options={sectionDateAgents}
                              optionFilterProp="label"
                            />
                            <Select
                              allowClear
                              showSearch
                              placeholder="All sections"
                              style={{ width: 220 }}
                              value={sectionDateSection}
                              onChange={setSectionDateSection}
                              options={sectionDateSections}
                              optionFilterProp="label"
                            />
                            <Checkbox
                              checked={onlyRepeatSections}
                              onChange={(e) => setOnlyRepeatSections(e.target.checked)}
                            >
                              Only {COACHING_THRESHOLD}+ errors on a section
                            </Checkbox>
                            {onlyRepeatSections && (
                              <Tag color={repeatSectionCount ? 'red' : 'default'}>
                                {repeatSectionCount} to coach
                              </Tag>
                            )}
                          </Space>
                        }
                      >
                        <Table
                          rowKey={(r) => `${r.SubmissionID}||${r.SectionName}`}
                          dataSource={sectionDateVisible}
                          loading={sectionDateLoading}
                          size="small"
                          pagination={{ defaultPageSize: 100, showSizeChanger: true, showTotal: (t) => `${t} rows` }}
                          scroll={{ x: 'max-content' }}
                          sticky={{ offsetHeader: STICKY_OFFSET }}
                          locale={{ emptyText: 'No completed error submissions in this date range.' }}
                          columns={[
                            { title: 'Error Submission ID', dataIndex: 'SubmissionID', key: 'SubmissionID', width: 170,
                              render: (v) => v == null ? <span style={{ color: '#d9d9d9' }}>—</span> : (
                                <Button type="link" size="small" style={{ padding: 0 }}
                                  onClick={() => navigate(`/errors/${v}`)}>{v}</Button>
                              ) },
                            { title: 'Date of Error', dataIndex: 'DateOfError', key: 'DateOfError', width: 120 },
                            { title: 'Agent Name', dataIndex: 'AgentName',   key: 'AgentName',   width: 180, ellipsis: true },
                            { title: 'Section',    dataIndex: 'SectionName', key: 'SectionName', width: 260, ellipsis: true },
                            { title: 'Errors',    dataIndex: 'TotalErrors',    key: 'TotalErrors',    width: 90,  align: 'right',
                              render: (v) => v > 0 ? <Tag color="red">{v}</Tag>    : <span style={{ color: '#d9d9d9' }}>—</span> },
                            { title: 'Feedbacks', dataIndex: 'TotalFeedbacks', key: 'TotalFeedbacks', width: 100, align: 'right',
                              render: (v) => v > 0 ? <Tag color="orange">{v}</Tag> : <span style={{ color: '#d9d9d9' }}>—</span> },
                            {
                              // Already returned by question-stats?group_by=section_date, which
                              // this table calls -- the error detail page renders the same
                              // field. A submission can raise more than one session, so this is
                              // a list rather than a single value.
                              title: 'Coaching Session', dataIndex: 'Coachings', key: 'Coachings', width: 260,
                              render: (list) => !list?.length
                                ? <span style={{ color: '#d9d9d9' }}>—</span>
                                : (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                    {list.map((c) => (
                                      <div key={c.ID} style={{ fontSize: 12, lineHeight: 1.4 }}>
                                        <Typography.Link onClick={() => window.open(`/coaching/sessions/${c.ID}`, '_blank')}>
                                          #{c.ID}
                                        </Typography.Link>
                                        {c.CreatedOn && <span style={{ color: '#8c8c8c' }}> · {c.CreatedOn}</span>}
                                        {c.RequestType && <span style={{ color: '#8c8c8c' }}> · {c.RequestType}</span>}
                                        {c.Linked != null && <span style={{ color: '#8c8c8c' }}> · {c.Linked ? 'Linked Sections' : 'All Sections'}</span>}
                                        <Tag color={SESSION_STATUS_COLOR[c.Status] ?? 'default'} style={{ marginLeft: 6 }}>
                                          {SESSION_STATUS_LABEL[c.Status] ?? c.Status}
                                        </Tag>
                                      </div>
                                    ))}
                                  </div>
                                ),
                            },
                            SPACER_COL,
                          ]}
                        />
                      </Card>
              </>
            ),
          },
        ]}
      />


      <Drawer
        open={!!statDrill}
        onClose={() => setStatDrill(null)}
        width={820}
        title={statDrill?.title || ''}
        destroyOnClose
      >
        <Table
          rowKey="ID"
          size="small"
          loading={!statDrill?.rows}
          dataSource={statDrill?.rows || []}
          pagination={{ defaultPageSize: 25, size: 'small', hideOnSinglePage: true }}
          scroll={{ x: 'max-content' }}
          onRow={(r) => ({ onClick: () => window.open(`/errors/${r.ID}`, '_blank', 'noopener'), style: { cursor: 'pointer' } })}
          columns={[
            { title: 'ID', dataIndex: 'ID', key: 'ID', width: 70, sorter: (a, b) => a.ID - b.ID,
              render: (v) => <Typography.Text strong style={{ color: '#1677ff' }}>#{v}</Typography.Text> },
            { title: 'Date of Error', dataIndex: 'DateOfError', key: 'DateOfError', width: 120,
              sorter: (a, b) => (a.DateOfError || '').localeCompare(b.DateOfError || '') },
            { title: 'Campaign', dataIndex: 'Campaign', key: 'Campaign', width: 140, render: (v) => v || '—' },
            { title: 'Form', dataIndex: 'FormName', key: 'FormName', width: 220 },
            { title: 'Errors', dataIndex: 'Errors', key: 'Errors', width: 80, align: 'right',
              render: (v) => (v > 0 ? <Tag color="red">{v}</Tag> : '—') },
            { title: 'Feedbacks', dataIndex: 'Feedbacks', key: 'Feedbacks', width: 95, align: 'right',
              render: (v) => (v > 0 ? <Tag color="orange">{v}</Tag> : '—') },
          ]}
        />
      </Drawer>

      <Drawer
        title={drillTitle}
        open={drillOpen}
        onClose={() => setDrillOpen(false)}
        width={960}
        destroyOnClose
      >
        <Table
          rowKey="ID"
          dataSource={drillRows}
          columns={DRILL_COLS(navigate)}
          loading={drillLoading}
          size="small"
          pagination={{ defaultPageSize: 25, showSizeChanger: true, showTotal: (t) => `${t} submissions` }}
          scroll={{ x: 'max-content' }}
          onRow={(r) => ({ onClick: () => navigate(`/errors/${r.ID}`), style: { cursor: 'pointer' } })}
        />
      </Drawer>
    </>
  );
}
